import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconCut, IconGif } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import VideoRangeSlider from '../../components/video/VideoRangeSlider';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    DitherGlyph,
    WidthGlyph,
    videoTileClass,
    videoTrayClass,
} from '../../components/video/VideoOptionTiles';
import {
    DEFAULT_DITHERING,
    DEFAULT_FPS,
    DEFAULT_SIZE,
    DITHERING_OPTIONS,
    GIF_SIZE_PRESETS,
    MAX_TO_GIF_VIDEO_BYTES,
    MIN_TRIM_GAP_SECONDS,
    formatFileSize,
    formatVideoTime,
    isSupportedVideoFile,
    toGifVideoName,
    videoToGif,
} from './toGifVideoEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

export default function ToGifVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);

    const [file, setFile] = useState(null);
    const [gifSize, setGifSize] = useState(DEFAULT_SIZE);
    const [fps, setFps] = useState(DEFAULT_FPS);
    const [dithering, setDithering] = useState(DEFAULT_DITHERING);
    const [duration, setDuration] = useState(0);
    const [trimRange, setTrimRange] = useState([0, 0]);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const clipDuration = Math.max(0, trimRange[1] - trimRange[0]);
    const trimStart = trimRange[0];
    const trimEnd = trimRange[1];

    const videoPreviewUrl = useMemo(
        () => (file ? URL.createObjectURL(file) : ''),
        [file],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    }, [videoPreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    const addFile = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('toGifVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        const next = videos[0];
        if (next.size > MAX_TO_GIF_VIDEO_BYTES) {
            const msg = t('toGifVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(next);
        setResult(null);
        setError(null);
        setProgress(null);
        setGifSize(DEFAULT_SIZE);
        setFps(DEFAULT_FPS);
        setDithering(DEFAULT_DITHERING);
        setDuration(0);
        setTrimRange([0, 0]);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFile, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(null);
        setGifSize(DEFAULT_SIZE);
        setFps(DEFAULT_FPS);
        setDithering(DEFAULT_DITHERING);
        setDuration(0);
        setTrimRange([0, 0]);
    };

    const invalidateResult = () => {
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const handleVideoLoaded = useCallback((e) => {
        const dur = e.target.duration;
        if (dur && Number.isFinite(dur)) {
            setDuration(dur);
            setTrimRange([0, dur]);
        }
    }, []);

    const handleTrimChange = useCallback((range, thumb = 'start') => {
        setTrimRange(range);
        invalidateResult();
        if (videoRef.current) {
            videoRef.current.currentTime = thumb === 'end' ? range[1] : range[0];
        }
    }, []);

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'OUT_OF_MEMORY') return t('toGifVideo.errors.outOfMemory');
        if (code === 'ENCODE_FAILED' || code === 'EMPTY_OUTPUT' || code === 'GIF_FAILED') {
            return t('toGifVideo.errors.failed');
        }
        return t('toGifVideo.errors.failed');
    };

    const handleConvert = async () => {
        if (!file || processing) return;

        recordToolUsage('video/to-gif', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const preset = GIF_SIZE_PRESETS.find((p) => p.id === gifSize);
            const trimDuration = trimEnd - trimStart;
            const blob = await videoToGif(file, {
                maxWidth: preset?.maxWidth ?? 480,
                fps,
                dithering,
                startTime: trimStart > 0 ? trimStart : 0,
                duration: duration > 0 && trimDuration < duration - 0.05 ? trimDuration : 0,
            }, (value) => setProgress(value));

            setResult({
                name: toGifVideoName(file.name),
                originalSize: file.size,
                outputSize: blob.size,
                blob,
            });
            notifications.show({
                title: t('common.success'),
                message: t('toGifVideo.done'),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`toGifVideo.howTo.step${i}`)),
        [t],
    );

    const sizeNote = result ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {formatFileSize(result.originalSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">→</span>
            <span className="font-semibold text-[#1E9E5E]">
                {formatFileSize(result.outputSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">GIF</span>
        </>
    ) : null;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('toGifVideo.done'),
    });

    const resultDownloads = result ? [{
        id: result.name,
        name: result.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.outputSize),
        onDownload: () => downloadBlob(result.blob, result.name),
    }] : undefined;

    return (
        <ToolPageLayout
            slug="video/to-gif"
            subtitleKey="toGifVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={false}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('toGifVideo.convert'),
                onClick: handleConvert,
                disabled: !file || processing || !!result,
                loading: processing,
                icon: <IconGif size={18} stroke={1.9} />,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <VideoDropZone
                            title={t('toGifVideo.dropTitle')}
                            hint={t('toGifVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <VideoFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={handleReset}
                            removeLabel={t('toGifVideo.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file ? (
                <div className="space-y-3">
                    <ToolCard title={t('toGifVideo.settingsTitle')}>
                        <div className="space-y-4">
                            <div>
                                <p className="mb-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('toGifVideo.sizeTitle')}
                                </p>
                                {/* Two rows of three: at six across, "মূল সাইজ" no longer fits a tile. */}
                                <div className={videoTrayClass('grid-cols-3')}>
                                    {GIF_SIZE_PRESETS.map((preset) => (
                                        <button
                                            key={preset.id}
                                            type="button"
                                            onClick={() => {
                                                setGifSize(preset.id);
                                                invalidateResult();
                                            }}
                                            title={t(`toGifVideo.sizes.${preset.id}`)}
                                            aria-pressed={gifSize === preset.id}
                                            className={videoTileClass(gifSize === preset.id)}
                                        >
                                            <WidthGlyph width={preset.maxWidth} />
                                            <span className="max-w-full truncate">{t(`toGifVideo.sizes.${preset.id}`)}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <label htmlFor="to-gif-fps" className="block space-y-1.5">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('toGifVideo.fpsLabel', { value: toBnDigits(fps) })}
                                </span>
                                <input
                                    id="to-gif-fps"
                                    type="range"
                                    min={5}
                                    max={20}
                                    step={1}
                                    value={fps}
                                    onChange={(e) => {
                                        setFps(Number(e.target.value));
                                        invalidateResult();
                                    }}
                                    className="w-full accent-violet-500"
                                />
                                <span className="block text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                    {t('toGifVideo.fpsHint')}
                                </span>
                            </label>

                            <div>
                                <p className="mb-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('toGifVideo.ditherTitle')}
                                </p>
                                {/* Each glyph draws a fade the way that method renders it;
                                    the name and trade-off sit under the row. */}
                                <div className={videoTrayClass('grid-cols-4')}>
                                    {DITHERING_OPTIONS.map((opt) => (
                                        <button
                                            key={opt}
                                            type="button"
                                            onClick={() => {
                                                setDithering(opt);
                                                invalidateResult();
                                            }}
                                            title={`${t(`toGifVideo.dither.${opt}`)} — ${t(`toGifVideo.dither.${opt}Desc`)}`}
                                            aria-label={t(`toGifVideo.dither.${opt}`)}
                                            aria-pressed={dithering === opt}
                                            className={videoTileClass(dithering === opt)}
                                        >
                                            <DitherGlyph method={opt} />
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-2 text-[11.5px] leading-snug text-[#A8A099] dark:text-zinc-500">
                                    <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                                        {t(`toGifVideo.dither.${dithering}`)}
                                    </span>
                                    {' — '}
                                    {t(`toGifVideo.dither.${dithering}Desc`)}
                                </p>
                            </div>
                        </div>
                    </ToolCard>

                    {duration > 0 ? (
                        <ToolCard title={t('toGifVideo.trimTitle')}>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                        <IconCut size={16} stroke={1.8} className="text-violet-500" />
                                        {t('toGifVideo.trimLabel')}
                                    </div>
                                    <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[11.5px] font-semibold text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
                                        {t('toGifVideo.selectedDuration', {
                                            time: toBnDigits(formatVideoTime(clipDuration)),
                                        })}
                                    </span>
                                </div>

                                <VideoRangeSlider
                                    min={0}
                                    max={duration}
                                    step={0.1}
                                    minRange={MIN_TRIM_GAP_SECONDS}
                                    value={trimRange}
                                    onChange={handleTrimChange}
                                    startLabel={t('toGifVideo.startLabel', {
                                        time: toBnDigits(formatVideoTime(trimStart)),
                                    })}
                                    endLabel={t('toGifVideo.endLabel', {
                                        time: toBnDigits(formatVideoTime(trimEnd)),
                                    })}
                                />

                                <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                    {t('toGifVideo.trimHint', {
                                        time: toBnDigits(formatVideoTime(duration)),
                                    })}
                                </p>
                            </div>
                        </ToolCard>
                    ) : null}
                </div>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('toGifVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toGifVideo.previewEmpty')}
                    idleHint={t('toGifVideo.previewEmptyHint')}
                    processingLabel={t('toGifVideo.converting')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={t('toGifVideo.done')}
                    downloadLabel={t('toGifVideo.download')}
                    downloads={resultDownloads}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('toGifVideo.startOver')}
                    sizeNote={sizeNote}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('toGifVideo.convert')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                >
                    {file && !processing && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                {result ? (
                                    <img
                                        src={resultPreviewUrl}
                                        alt={result.name}
                                        className="max-h-[360px] w-full max-w-full object-contain"
                                    />
                                ) : (
                                    <video
                                        ref={videoRef}
                                        src={videoPreviewUrl}
                                        controls
                                        onLoadedMetadata={handleVideoLoaded}
                                        className="max-h-[360px] w-full max-w-full"
                                    />
                                )}
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result ? result.name : file.name}
                                </span>
                                <span>
                                    {result
                                        ? formatFileSize(result.outputSize)
                                        : duration > 0
                                            ? toBnDigits(formatVideoTime(clipDuration || duration))
                                            : formatFileSize(file.size)}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
